import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { PlusCircle, Edit2, Trash2, FileText } from 'lucide-react';
import Sidebar, { SidebarProvider } from '../../components/navigation/Sidebar';
import { getAllPrompts, deletePrompt } from '../../services/wordpressService';
import { format } from 'date-fns';
import { useModal } from '../../components/ui/ModalProvider';
import { useLanguageProfile } from '../../contexts/LanguageProfileContext';
import { useRequestGuard } from '../../hooks/useRequestGuard';

// Prompt type label mapping
const PROMPT_TYPE_LABELS = {
  'wordpress article': 'WordPress Article',
  'facebook cta': 'Facebook CTA',
  'facebook post': 'Facebook Post',
  'pinterest cta': 'Pinterest CTA',
  'image': 'Image',
  'image description': 'Image Description',
  'title': 'Title',
  'keyword': 'Keyword',
  'article': 'Article',
  'midjourney': 'MidJourney',
  'seo': 'SEO',
  'custom': 'Custom',
};

export default function Prompts() {
  const navigate = useNavigate();
  const { confirm } = useModal();
  const { activeProfile } = useLanguageProfile();
  const startPromptsRequest = useRequestGuard();
  const [prompts, setPrompts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [deletingId, setDeletingId] = useState(null);

  // Clear the previous profile's prompts immediately on switch so stale
  // content can't flash while the new profile's data is loading.
  useEffect(() => {
    setPrompts([]);
  }, [activeProfile?.id]);

  useEffect(() => {
    loadPrompts();
  }, [activeProfile?.id]);

  const loadPrompts = async () => {
    const isCurrent = startPromptsRequest();
    try {
      setIsLoading(true);
      const data = await getAllPrompts(activeProfile?.id);
      if (!isCurrent()) return;
      setPrompts(data);
      setError('');
    } catch (err) {
      if (!isCurrent()) return;
      setError(err?.message || 'Failed to load prompts');
    } finally {
      if (isCurrent()) setIsLoading(false);
    }
  };

  const handleDelete = async (promptId) => {
    if (!(await confirm('Are you sure you want to delete this prompt?'))) {
      return;
    }

    try {
      setDeletingId(promptId);
      await deletePrompt(promptId);
      setPrompts(prompts?.filter(p => p?.id !== promptId));
    } catch (err) {
      setError(err?.message || 'Failed to delete prompt');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <SidebarProvider>
      <div className="min-h-screen bg-background">
        <Sidebar />
        <main className="main-content">
          <div className="max-w-7xl mx-auto">
            {/* Header */}
            <div className="flex items-center justify-between mb-8">
              <div>
                <h1 className="text-3xl font-bold text-text">Prompts Library</h1>
                <p className="text-text-secondary mt-2">
                  Manage your AI prompt templates for content generation
                </p>
              </div>
              <button
                onClick={() => navigate('/tools/add-prompt')}
                className="px-6 py-3 bg-primary hover:bg-primary-hover text-white font-medium rounded-lg transition-colors flex items-center gap-2"
              >
                <PlusCircle className="w-5 h-5" />
                Create New Prompt
              </button>
            </div>

            {/* Error Message */}
            {error && (
              <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
                <p className="text-red-500 text-sm">{error}</p>
              </div>
            )}

            {/* Loading State */}
            {isLoading ? (
              <div className="flex items-center justify-center py-20">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
              </div>
            ) : prompts?.length === 0 ? (
              /* Empty State */
              (<div className="bg-surface rounded-lg border border-border p-12 text-center">
                <FileText className="w-16 h-16 text-text-secondary mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-text mb-2">No Prompts Yet</h3>
                <p className="text-text-secondary mb-6">
                  Create your first AI prompt template to get started
                </p>
                <button
                  onClick={() => navigate('/tools/add-prompt')}
                  className="px-6 py-3 bg-primary hover:bg-primary-hover text-white font-medium rounded-lg transition-colors inline-flex items-center gap-2"
                >
                  <PlusCircle className="w-5 h-5" />
                  Create Your First Prompt
                </button>
              </div>)
            ) : (
              /* Prompts Grid */
              (<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {prompts?.map((prompt) => (
                  <div
                    key={prompt?.id}
                    className="bg-surface rounded-lg border border-border p-6 hover:border-primary/50 transition-colors"
                  >
                    {/* Prompt Header */}
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-text mb-1 line-clamp-2">
                          {prompt?.name}
                        </h3>
                        <div className="inline-flex items-center px-3 py-1 bg-primary/10 text-primary text-sm font-medium rounded-full">
                          {PROMPT_TYPE_LABELS?.[prompt?.promptType] || prompt?.promptType}
                        </div>
                      </div>
                    </div>

                    {/* Prompt Content Preview */}
                    <p className="text-text-secondary text-sm mb-4 line-clamp-3">
                      {prompt?.content}
                    </p>

                    {/* Prompt Meta */}
                    <div className="flex items-center justify-between text-xs text-text-secondary mb-4 pt-4 border-t border-border">
                      <span>
                        Created {format(new Date(prompt?.createdAt), 'MMM d, yyyy')}
                      </span>
                      {prompt?.isDefault && (
                        <span className="px-2 py-1 bg-blue-500/10 text-blue-500 rounded">
                          Default
                        </span>
                      )}
                    </div>

                    {/* Action Buttons */}
                    {!prompt?.isDefault && (
                      <div className="flex gap-2">
                        <button
                          onClick={() => navigate(`/tools/edit-prompt/${prompt?.id}`)}
                          className="flex-1 px-4 py-2 bg-surface-hover hover:bg-surface border border-border text-text rounded-lg transition-colors flex items-center justify-center gap-2 text-sm font-medium"
                        >
                          <Edit2 className="w-4 h-4" />
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(prompt?.id)}
                          disabled={deletingId === prompt?.id}
                          className="flex-1 px-4 py-2 bg-red-500/10 hover:bg-red-500/20 border border-red-500/20 text-red-500 rounded-lg transition-colors flex items-center justify-center gap-2 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <Trash2 className="w-4 h-4" />
                          {deletingId === prompt?.id ? 'Deleting...' : 'Delete'}
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>)
            )}
          </div>
        </main>
      </div>
    </SidebarProvider>
  );
}
